/*
    Examiner test page
*/

import React, { useEffect, useRef, useState } from 'react';
import Dashboard from '../dashboard';
import {
  PollingContext,
  useCurrentItem,
  useData,
  useDataActions,
  useIsAdministeringTest,
  useTimestamps
} from '../item-content/polling-context';
import styles from '../../style/examiner/test-page.module.css';
import ItemContent from '../item-content/item-content';
import ItemContentContext from '../item-content/item-content-context';
import Timer from '../item-content/timer';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import SizeWrapper from '../size-wrapper';
import { useHistory, useParams } from 'react-router-dom';
import { buildNavigationData } from './test-page-navigation-data';

// PIN-121: fallback escape for navInFlight when no server signal (timestamp
// advance or isAdministeringTest=false) arrives. Without it, mid-subtest
// End Test on ANCR leaves all nav buttons permanently disabled. Overlay
// dismissal is covered transitively — Next Subtest advances the timestamp,
// End Test flips isAdministeringTest — so the effect doesn't need a
// separate check for it. Safe because `updateDataAndFlush` in
// polling-context.js documents that wici-api is idempotent on
// sessionControl_* keys within the same currentItemTimestamp window — so
// a late server response after the watchdog can't double-write.
const NAV_WATCHDOG_MS = 10_000;

// PIN-200 #2 — a recordDataKey carries a 1-based position suffix
// ("wcat_item44_dzh_3") so a word whose first and last target sound repeat
// (item 44 "Judge" /dʒ/ /ǝ/ /dʒ/, item 45 "Church" /tʃ/ /ǝɹ/ /tʃ/) gets one
// addressable key per occurrence rather than two buttons sharing one session
// field. soundLabels stays keyed by the bare slug, since both occurrences are
// the same sound. The suffix is optional: items seeded before PIN-200 still
// carry un-suffixed keys and must keep rendering.
const SOUND_ERROR_KEY_RE = /^wcat_item\d+_([a-zA-Z]+)(?:_(\d+))?$/;

const parseSoundErrorKey = (key) => {
  const match = SOUND_ERROR_KEY_RE.exec(key);
  return match ? { slug: match[1], position: match[2] } : { slug: key.split('_').pop() };
};

// Labels for one item's sound-error buttons, in recordDataKeys order — always
// the bare IPA glyph.
//
// PIN-200 appended the occurrence position to a repeated sound ("dʒ (1)" /
// "dʒ (3)") so the two buttons on items 44 ("Judge") and 45 ("Church") read
// distinctly. PIN-205 #2 removes those numbers at the publisher's request:
// the buttons are fixed 1.75rem squares that the suffix overflows, and the
// examiner already reads position from their left-to-right order — the first
// is the word's initial sound, the last its final one.
//
// Only the label changes. Both buttons remain, keyed by the position-suffixed
// recordDataKey, because PIN-200 needs one addressable key per occurrence for
// /dʒ/ and /tʃ/ to tally the possible: 2 their SOUND_INVENTORY entry claims.
const buildSoundErrorLabels = (keys, soundLabels) =>
  keys.map((key) => {
    const { slug } = parseSoundErrorKey(key);
    return soundLabels?.[slug] ?? slug.toUpperCase();
  });

const TestPageContent = () => {
  const history = useHistory();

  const currentItem = useCurrentItem();
  const data = useData();
  const { setSessionID, updateData, updateDataAndFlush } = useDataActions();
  const isAdministeringTest = useIsAdministeringTest();
  const { currentItemTimestamp } = useTimestamps();

  // WCAT sound-error keys (PIN-193): item.recordDataKeys holds one key per
  // target phoneme, e.g. "wcat_item4_w" — seeded by wcat-data-seeding from
  // each item's real examiner content. No other battery populates this
  // field, so its presence is what identifies a WCAT scored item here.
  const soundErrorKeys = currentItem?.recordDataKeys ?? [];
  const hasSoundError = soundErrorKeys.some((key) => data[key] === true);
  const soundErrorLabels = buildSoundErrorLabels(soundErrorKeys, currentItem?.soundLabels);

  // Session data's actual battery field is `battery_name` (models/session.js
  // sets it from record.test.battery.name, e.g. "ANCR"/"STARS"/"WCAT") — NOT
  // `battery_type`, which nothing in wici-api has ever populated. Matched
  // with startsWith rather than strict equality, mirroring wici-api's own
  // isANCR/isSTARS/isWCAT helpers (helpers/scoring/*.js), which check
  // report.name the same way — battery/report names are env-configurable
  // but conventionally start with the battery's fixed prefix.
  const isAncr = !!data.battery_name?.startsWith('ANCR');
  const isWcat = !!data.battery_name?.startsWith('WCAT');

  const { sessionID } = useParams();

  const [responseButtonSize, setResponseButtonSize] = useState('25%');
  // Visual disabled feedback for nav buttons while a flush is pending.
  // Mirrors the navGuard ref so both header and overlay buttons show the
  // same in-flight state.
  const [navInFlight, setNavInFlight] = useState(false);

  // Synchronous click-suppression guard. Captures currentItemTimestamp at
  // click time; subsequent clicks are no-op'd until the server-confirmed
  // timestamp advances (or the overlay is dismissed). This is read inside
  // the click handler before any setState — a re-fired click in the same
  // React batch sees the captured value and bails, regardless of whether
  // the disabled attribute has been committed yet. Shared across header
  // and overlay nav buttons so all four routes through dispatchNav are
  // protected.
  //
  // Invariant: `navGuard.current === null` means "no capture." This is safe
  // because polling-context initializes currentItemTimestamp to `0` (number)
  // and never sets it to null — see `useState(0)` in polling-context.js.
  // If that ever changes, switch to a separate `hasGuard` boolean ref.
  const navGuard = useRef(null);

  const noteRef = useRef();
  const stopTimer = useRef(() => {});

  // init
  useEffect(() => {
    setSessionID(sessionID);
  }, [sessionID, setSessionID]);

  // new current item
  useEffect(() => {
    // determine response button size
    if (currentItem != null && currentItem.allowedResponses != null) {
      setResponseButtonSize(`${100 / Math.ceil(Math.sqrt(currentItem.allowedResponses.length))}%`);
    }

    // reset scroll positions to top.
    if (noteRef.current) noteRef.current.scrollTop = 0;
  }, [currentItem]);

  // redirect to session list page if not administering test
  useEffect(() => {
    if (isAdministeringTest === false) history.replace('/examiner/sessions');
  }, [history, isAdministeringTest]);

  // Unified navInFlight escape (PIN-42 + PIN-121). Clears the in-flight gate
  // when any recovery signal fires:
  //   - currentItemTimestamp advanced past the captured guard (covers normal
  //     nav AND overlay Next Subtest, which lands on the new subtest's first
  //     item).
  //   - isAdministeringTest flipped false (covers BOTH the overlay End Test
  //     path AND the mid-subtest End Test path that PIN-121 reported as
  //     locking up — the session is torn down server-side without any item
  //     advance).
  //   - NAV_WATCHDOG_MS elapsed (last-resort safety net so the UI is never
  //     permanently locked regardless of network/server timing; safe per the
  //     idempotency contract documented on `updateDataAndFlush` in
  //     polling-context.js).
  useEffect(() => {
    if (!navInFlight) return;
    const released =
      (navGuard.current !== null && navGuard.current !== currentItemTimestamp) ||
      isAdministeringTest === false;
    if (released) {
      setNavInFlight(false);
      navGuard.current = null;
      return;
    }
    const watchdog = setTimeout(() => {
      // If this fires in production, the server never signaled session end
      // (no timestamp advance, isAdministeringTest never flipped). Surface it
      // so the underlying root cause is investigable rather than masked.
      // eslint-disable-next-line no-console
      console.warn('[PIN-121] navInFlight watchdog fired — no server signal within 10s');
      setNavInFlight(false);
      navGuard.current = null;
    }, NAV_WATCHDOG_MS);
    return () => clearTimeout(watchdog);
  }, [navInFlight, currentItemTimestamp, isAdministeringTest]);

  // Unified nav dispatch for all session-control buttons (header + overlay).
  // Always flushes pending polls so the local nav write reaches the server
  // before any in-flight stale state can clobber it. The ref-based guard
  // suppresses synchronously re-fired clicks within the same currentItemTimestamp
  // window; navInFlight gives the user-visible disabled feedback in both header
  // and overlay flows.
  const dispatchNav = (payload) => {
    if (navGuard.current !== null && navGuard.current === currentItemTimestamp) return;
    navGuard.current = currentItemTimestamp;
    setNavInFlight(true);
    stopTimer.current();
    updateDataAndFlush(buildNavigationData(payload));
  };

  // WCAT auto-advance (PIN-193), two round trips rather than one bundled
  // dispatchNav call. wici-api's Session#nextItem sets discontinueTriggered
  // then immediately clears it and ends the test in the SAME request when
  // discontinueEndsTest is true (WCAT Screener) — so a score/error change
  // bundled with sessionControl_nextItem in one call never lets the client
  // observe the intermediate discontinueTriggered:true state, and the
  // discontinue-overlay message never renders. Sending the change alone
  // first (awaiting the server-confirmed result) mirrors how ANCR's score
  // buttons and Next button are already two separate calls, giving the
  // overlay a request to render on for the one item that ends the test.
  const advanceAfterScoring = async (changes) => {
    const result = await updateDataAndFlush(changes);
    // undefined means the flush was cancelled or never reached the server
    // (offline, a superseding click) — server state is unknown, so stay put
    // and let the next poll tick resolve it rather than advancing blind.
    // discontinueTriggered: the overlay is now showing (or the test already
    // ended) — don't auto-advance. The examiner uses the overlay's own
    // Next Subtest / End Test buttons, or isAdministeringTest flips false.
    if (result === undefined || result.discontinueTriggered === true) return;
    dispatchNav({ sessionControl_nextItem: true });
  };

  return (
    <Dashboard>
      <div className={styles['card']}>
        <div className={styles['card__header']}>
          {data.examinee_firstName || data.examinee_lastName ? (
            <label className={styles['name']}>
              {data.examinee_firstName} {data.examinee_lastName}
            </label>
          ) : null}
          <label className={styles['session-id-label']}>Session ID</label>
          <div className={styles['session-id-border']}>
            <label className={styles['session-id']}>{sessionID}</label>
          </div>
          <label className={styles['item-label-label']}>Item</label>
          <div className={styles['item-label-border']}>
            <label className={styles['item-label']}>{currentItem ? currentItem.label : ''}</label>
          </div>
          {data.showAnswerEligibleForCurrentItem === true &&
            (data.showAnswerCandidatesRemaining ?? 0) > 0 && (
              <button
                className={styles['end-test'] + ' ' + styles['show-answer']}
                data-testid="examiner-show-answer"
                onClick={() => updateData({ sessionControl_showAnswer: true })}
              >
                Show Answer ({data.showAnswerCandidatesRemaining} left)
              </button>
            )}
          {
            // PIN-198 #9: WCAT is one subtest per test — there is never a
            // "next test"/"next subtest" to advance to, so this button is
            // omitted entirely rather than shown with a misleading label.
            !isWcat && (
              <button
                className={styles['end-test'] + ' ' + styles['next-test']}
                disabled={navInFlight}
                onClick={() => dispatchNav({ sessionControl_nextTest: true })}
              >
                {isAncr ? 'Next Subtest' : 'Next Test'}
              </button>
            )
          }
          <button
            className={styles['end-test']}
            disabled={navInFlight}
            onClick={() => dispatchNav({ sessionControl_endTest: true })}
          >
            End Test
          </button>
        </div>
        <hr className={styles['card__line']}></hr>
        <div className={styles['card__main']}>
          <div
            className={`${styles['discontinue-overlay']}${
              data.discontinueTriggered ? ' ' + styles['active'] : ''
            }`}
          >
            <p className={styles['discontinue-message']}>{data.discontinueMessage}</p>
            <div className={styles['discontinue-buttons']}>
              {
                // PIN-198 #8: WCAT Screener's ceiling ends the session
                // outright (discontinueEndsTest: true, PIN-192) — there is
                // no next subtest to advance to, so offering this button
                // for WCAT sends sessionControl_nextTest on a single-subtest
                // battery, which restarted the test and confused the
                // examiner. Only End Test is offered for WCAT.
                !isWcat && (
                  <button
                    className={`${styles['end-test']} ${styles['next-test']}`}
                    disabled={navInFlight}
                    onClick={() => dispatchNav({ sessionControl_nextTest: true })}
                  >
                    Next Subtest
                  </button>
                )
              }
              <button
                className={styles['end-test']}
                disabled={navInFlight}
                onClick={() => dispatchNav({ sessionControl_endTest: true })}
              >
                End Test
              </button>
            </div>
          </div>
          <div className={styles['card__body']}>
            <ItemContentContext examiner interactionEnabled readOnly>
              <ItemContent />
            </ItemContentContext>
          </div>
          <hr className={styles['card__line']}></hr>
          <div className={styles['card__controls-container']}>
            <div className={`${styles['navigation-container']} ${styles['left']}`}>
              <button
                className={styles['navigation-button']}
                onClick={() => {
                  stopTimer.current();
                  updateData(buildNavigationData({ sessionControl_prevItem: true }));
                }}
                disabled={data.session_isFirstItem}
              >
                <ArrowLeftOutlined />
                Prev
              </button>
            </div>
            {
              // note
              <div className={styles['note-container']}>
                <label className={styles['input-label']}>Note</label>
                <textarea
                  className={styles['note']}
                  onChange={(e) => updateData({ response_note: e.target.value })}
                  ref={noteRef}
                  value={data.response_note || ''}
                />
              </div>
            }
            {
              // timer
              currentItem && (currentItem.timed || currentItem.timer_autoStart) && (
                <Timer
                  stop={stopTimer}
                  time={data.response_duration}
                  onTimeChange={(sec) => updateData({ response_duration: sec })}
                />
              )
            }
            {
              // button input
              currentItem &&
                currentItem.scored === true &&
                currentItem.allowedResponses != null && (
                  <div className={styles['score-container--buttons']}>
                    {currentItem.allowedResponses.map((allowedResponse, i) => {
                      var isSelected = false;

                      if (
                        allowedResponse.response === data.response_response &&
                        allowedResponse.score === data.response_score
                      )
                        isSelected = true; // response and score match
                      if (
                        allowedResponse.response === '' &&
                        allowedResponse.score === data.response_score
                      )
                        isSelected = true; // no allowed response, score matches
                      if (
                        allowedResponse.response === data.response_response &&
                        allowedResponse.score === null
                      )
                        isSelected = true; // response matches, no allowed score

                      // Mirrors the onClick guard below: a "1" can't be
                      // scored while a sound error is selected. Reflected in
                      // disabled so the button doesn't look clickable while
                      // silently doing nothing (PIN-198 #5).
                      const blockedByHasSoundError =
                        !isSelected &&
                        soundErrorKeys.length > 0 &&
                        allowedResponse.score === 1 &&
                        hasSoundError;

                      return (
                        <div
                          key={'buttonContainer' + i}
                          style={{
                            height: responseButtonSize,
                            width: responseButtonSize
                          }}
                        >
                          <SizeWrapper key={'sizeWrapper' + i}>
                            <button
                              className={`${styles['score-button']} ${isSelected ? styles['selected'] : ''}`}
                              disabled={blockedByHasSoundError}
                              key={i}
                              onClick={() => {
                                const newResponse = isSelected ? null : allowedResponse.response;
                                const newScore = isSelected ? null : allowedResponse.score;

                                // WCAT sound-error items (PIN-193, revised PIN-198):
                                //  - Score "1" is blocked while any sound error is
                                //    selected — a word with an error can't be scored
                                //    correct (PIN-198 #5). Deselecting the score button
                                //    (isSelected) is still always allowed.
                                //  - Score "1" (no error selected) still auto-advances.
                                //  - Score "0" NEVER auto-advances, regardless of click
                                //    order — the examiner must mark every sound error via
                                //    the Next button, which is itself gated until at
                                //    least one error is selected (PIN-198 #1/#6).
                                if (!isSelected && soundErrorKeys.length > 0) {
                                  if (newScore === 1 && hasSoundError) return;
                                  if (newScore === 1) {
                                    advanceAfterScoring({
                                      response_response: newResponse,
                                      response_score: newScore
                                    });
                                    return;
                                  }
                                }

                                updateData({
                                  response_response: newResponse,
                                  response_score: newScore
                                });
                              }}
                            >
                              {allowedResponse.response || allowedResponse.score}
                            </button>
                          </SizeWrapper>
                        </div>
                      );
                    })}
                  </div>
                )
            }
            {
              // score input
              currentItem &&
                currentItem.scored === true &&
                currentItem.allowedResponses == null && (
                  <div className={styles['score-container--text']}>
                    <label className={styles['input-label']}>Score</label>
                    <input
                      className={styles['score-input']}
                      type="number"
                      value={data['response_score'] === 0 ? 0 : data['response_score'] || ''}
                      onChange={(e) => {
                        updateData({
                          response_score: parseFloat(e.target.value)
                        });
                      }}
                    />
                  </div>
                )
            }
            {
              // response input
              currentItem &&
                currentItem.scored === true &&
                currentItem.allowedResponses == null && (
                  <div className={styles['response-container']}>
                    <label className={styles['input-label']}>Response</label>
                    <textarea
                      className={styles['response']}
                      onChange={(e) => updateData({ response_response: e.target.value })}
                      value={data.response_response || ''}
                    />
                  </div>
                )
            }
            {
              // The examiner-facing "G" guess toggle lived here. The authors
              // withdrew the Guess option (PIN-199 #5), and unlike the
              // examinee questionnaire's control this one was gated on a
              // hardcoded isStars check rather than the test's allowGuess
              // flag, so turning the flag off would not have hidden it.
            }
            {
              // WCAT sound-error toggles (PIN-193, revised PIN-198). One
              // button per target phoneme on this item (item.recordDataKeys).
              // Label prefers item.soundLabels[slug] — the real IPA glyph
              // seeded alongside recordDataKeys — matching the notation on
              // the item's own "Correct Response:" line (PIN-198 #2);
              // recordDataKeys itself must stay ASCII, so falls back to the
              // ASCII slug if soundLabels is missing (e.g. stale seed data).
              // A sound repeated within the word also carries its position
              // (PIN-200 #2) — see buildSoundErrorLabels above.
              // Selecting any error forces the item score to 0 (PIN-198 #5)
              // and never auto-advances (PIN-198 #6) — the examiner marks
              // every error, then advances via the Next button, which is
              // gated until at least one error is selected.
              currentItem && currentItem.scored === true && soundErrorKeys.length > 0 && (
                <div className={styles['sound-error-container']} data-testid="wcat-sound-errors">
                  {soundErrorKeys.map((key, index) => {
                    const label = soundErrorLabels[index];
                    const isSelected = data[key] === true;

                    return (
                      <button
                        className={`${styles['sound-error-button']}${
                          isSelected ? ' ' + styles['selected'] : ''
                        }`}
                        data-testid={`wcat-sound-error-${key}`}
                        key={key}
                        onClick={() => {
                          const willBeTrue = !isSelected;
                          // Deselecting the last remaining error would
                          // otherwise leave response_score: 0 with no error
                          // selected — a state Next's disabled rule can't
                          // distinguish from "not yet scored," silently
                          // locking navigation with no visible explanation.
                          const stillHasError = soundErrorKeys.some(
                            (otherKey) => otherKey !== key && data[otherKey] === true
                          );

                          updateData(
                            willBeTrue
                              ? { [key]: true, response_score: 0 }
                              : { [key]: null, ...(stillHasError ? {} : { response_score: null }) }
                          );
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )
            }
            <div
              className={styles['examinee-preview-container']}
              onMouseDown={(event) => {
                event.preventDefault();
                window.open(`${window.location.origin}/examinee/test/${sessionID}`, '_blank');
              }}
            >
              <ItemContentContext examinee interactionEnabled={false} readOnly>
                <ItemContent passive />
              </ItemContentContext>
            </div>
            <div className={`${styles['navigation-container']} ${styles['right']}`}>
              <button
                className={styles['navigation-button']}
                data-testid="examiner-next"
                onClick={() => {
                  stopTimer.current();
                  // WCAT items can carry an unflushed score/sound-error write
                  // (both now use plain updateData — PIN-198 #6 forbids
                  // auto-advance on 0). Bundling that with sessionControl_
                  // nextItem in one request would let wici-api's nextItem()
                  // silently clear discontinueTriggered before the client
                  // ever observes it (see models/session.js), skipping the
                  // ceiling overlay entirely. Flushing via advanceAfterScoring
                  // first — same two-round-trip pattern PIN-193 already uses
                  // for the auto-advance paths above — keeps that working.
                  //
                  // Only scored items can carry such a write, so gate on
                  // currentItem.scored too, not just isWcat: the Copyright/
                  // Directions front matter is unscored, so advanceAfterScoring({})
                  // has nothing to flush — sendLocalChanges no-ops on an empty
                  // localChanges buffer (polling-context.js) — and Next would
                  // never actually advance past those items.
                  if (isWcat && currentItem?.scored === true) {
                    // Mirror dispatchNav's user-visible disabled feedback for
                    // this pre-dispatch flush stage too, so a second click
                    // during the round trip can't fire an overlapping chain.
                    setNavInFlight(true);
                    advanceAfterScoring({});
                    return;
                  }
                  updateData(buildNavigationData({ sessionControl_nextItem: true }));
                }}
                // PIN-198 #1: a WCAT item scored "0" can't advance via Next
                // until at least one sound error is selected — mirrors the
                // score/sound-error handlers' own advance rule above.
                disabled={
                  navInFlight ||
                  data.session_isLastItem ||
                  (soundErrorKeys.length > 0 && data.response_score === 0 && !hasSoundError)
                }
              >
                <ArrowRightOutlined />
                Next
              </button>
            </div>
          </div>
          {
            // PIN-205 #1 — on the final item Next is disabled with nothing
            // saying why, so point the examiner at End Test. Seeded on the
            // test doc (wici-api models/test.js) and copied into session data
            // by Session.startTest, exactly like discontinueMessage above, so
            // the publisher can reword it without a deploy.
            //
            // Gated on the message being present rather than on the battery:
            // the field is generic, and wici-api sends '' for a test that
            // seeds none, so an unseeded test renders nothing on its own.
            data.session_isLastItem && data.lastItemMessage && (
              <p className={styles['last-item-message']} data-testid="last-item-message">
                {data.lastItemMessage}
              </p>
            )
          }
        </div>
      </div>
    </Dashboard>
  );
};

// a wraper for applying data context
const TestPage = () => {
  return (
    <PollingContext>
      <TestPageContent />
    </PollingContext>
  );
};

export default TestPage;
